import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
  ChevronLeft,
  Save,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  CreditCard,
  Building2,
  Briefcase,
  Hash,
  DollarSign,
} from "lucide-react";

const AmexCardApplicationForm = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [formData, setFormData] = useState({
    nombreAsesor: "",
    tarjetaAplicando: "",
    nombres: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    fechaNacimiento: "",
    correoElectronico: "",
    telefonoCelular: "",
    domicilioCalleNumero: "",
    numeroInterior: "",
    colonia: "",
    codigoPostal: "",
    delegacionMunicipio: "",
    ciudad: "",
    estado: "",
    tiempoViviendoAnos: "",
    tiempoViviendoMeses: "",
    tipoVivienda: "",
    paisNacimiento: "",
    estadoNacimiento: "",
    rfc: "",
    genero: "",
    numeroINE: "",
    empresaTrabaja: "",
    estatusLaboral: "",
    ocupacion: "",
    tipoIndustria: "",
    antiguedad: "",
    ingresoFijoMensual: "",
    esSocioClubPremier: "",
    numeroSocioClubPremier: "",
    tarjetasCreditoActuales: "",
    limiteCreditoMayor: "",
    ultimos4DigitosTarjeta: "",
    tieneCreditoAutomotriz: "",
    tieneCreditoHipotecario: "",
    scoreBuroCredito: "",
    notas: "",
  });

  const tarjetaOptions = [
    "Platino Crédito",
    "Platinum Servicios (Metálica)",
    "Aeroméxico Platinum Servicios",
    "Gold Servicios (Metálica)",
    "Gold Servicios (Rosa)",
    "Gold Servicios Business (Metálica) (Para Personas Físicas con Act Empresarial)",
  ];

  const tipoViviendaOptions = [
    "Rentada",
    "Propia",
    "Hipoteca",
    "Vivienda de un familiar",
  ];

  const generoOptions = ["Masculino", "Femenino"];

  const estatusLaboralOptions = [
    "Empleado",
    "Trabajador independiente",
    "Pensionado",
    "Negocio Propio",
    "Estudiante",
    "Ama/o de casa",
  ];

  const ocupacionOptions = [
    "Director, Gerente o Supervisor",
    "Contador",
    "Asesor",
    "Otros",
  ];

  const tipoIndustriaOptions = [
    "Finanzas, Seguros, Banca",
    "Servicios comerciales y profesionales",
    "Bienes Raíces",
    "Gobierno, Servicio Público",
    "Construcción",
    "Comercio",
    "Telecomunicaciones",
    "Petróleo, Gas, Minería",
    "Cuidados de la salud",
    "Transporte",
    "Educación",
    "Turismo",
    "Alimentos y bebidas",
    "Recreación, Deporte, Entretenimiento",
    "Otros",
  ];

  const siNoOptions = ["Sí", "No"];

  const estadosMexico = [
    "Aguascalientes",
    "Baja California",
    "Baja California Sur",
    "Campeche",
    "Chiapas",
    "Chihuahua",
    "Ciudad de México",
    "Coahuila",
    "Colima",
    "Durango",
    "Estado de México",
    "Guanajuato",
    "Guerrero",
    "Hidalgo",
    "Jalisco",
    "Michoacán",
    "Morelos",
    "Nayarit",
    "Nuevo León",
    "Oaxaca",
    "Puebla",
    "Querétaro",
    "Quintana Roo",
    "San Luis Potosí",
    "Sinaloa",
    "Sonora",
    "Tabasco",
    "Tamaulipas",
    "Tlaxcala",
    "Veracruz",
    "Yucatán",
    "Zacatecas",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const requiredFields = [
      "tarjetaAplicando",
      "nombres",
      "apellidoPaterno",
      "fechaNacimiento",
      "correoElectronico",
      "telefonoCelular",
      "domicilioCalleNumero",
      "colonia",
      "codigoPostal",
      "delegacionMunicipio",
      "ciudad",
      "estado",
      "tiempoViviendoAnos",
      "tipoVivienda",
      "paisNacimiento",
      "estadoNacimiento",
      "rfc",
      "genero",
      "numeroINE",
      "empresaTrabaja",
      "estatusLaboral",
      "ocupacion",
      "tipoIndustria",
      "antiguedad",
      "ingresoFijoMensual",
      "esSocioClubPremier",
      "tarjetasCreditoActuales",
      "limiteCreditoMayor",
      "ultimos4DigitosTarjeta",
      "tieneCreditoAutomotriz",
      "tieneCreditoHipotecario",
      "scoreBuroCredito",
    ];

    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === "") {
        return `El campo ${field} es requerido`;
      }
    }

    if (formData.esSocioClubPremier === "Sí" && !formData.numeroSocioClubPremier?.trim()) {
      return "El número de socio Club Premier es requerido si eres socio";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setMessage({ text: validationError, type: "error" });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const cardData = {
        ...formData,
        userId: user.uid,
        userEmail: user.email,
        tipoTarjeta: "amex",
        estado: "pendiente",
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, "cardApplications"), cardData);

      setMessage({
        text: "¡Solicitud enviada exitosamente! Te contactaremos pronto.",
        type: "success",
      });

      setTimeout(() => {
        navigate("/cards");
      }, 2000);
    } catch (error) {
      console.error("Error al enviar solicitud:", error);
      setMessage({
        text: "Error al enviar la solicitud. Por favor, intenta nuevamente.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Acceso no autorizado
          </h2>
          <p className="text-[#096365]">Por favor inicia sesión para continuar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/cards")}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-md">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-center gap-3">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/2052px-American_Express_logo_%282018%29.svg.png"
                  alt="American Express"
                  className="h-6 object-contain"
                />
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">
                    Solicitud de Tarjeta
                  </h1>
                  <p className="text-xs text-slate-600">
                    Completa el formulario para solicitar tu tarjeta
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Message Alert */}
        {message.text && (
          <div
            className={`mb-8 p-4 rounded-xl border-2 flex items-center gap-3 ${
              message.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="font-medium">{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Información del Asesor */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Información del Asesor
              </h2>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Nombre del asesor que le envió ésta solicitud
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  name="nombreAsesor"
                  value={formData.nombreAsesor}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  placeholder="Nombre del asesor"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                ¿Para cuál tarjeta está aplicando? <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select
                  name="tarjetaAplicando"
                  value={formData.tarjetaAplicando}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                  required
                >
                  <option value="">Selecciona una tarjeta</option>
                  {tarjetaOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Datos Personales */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Datos Personales
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nombre(s) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nombres"
                  value={formData.nombres}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Apellido Paterno <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="apellidoPaterno"
                  value={formData.apellidoPaterno}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Apellido Materno
                </label>
                <input
                  type="text"
                  name="apellidoMaterno"
                  value={formData.apellidoMaterno}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Fecha de Nacimiento (DD/MM/AAAA) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    name="fechaNacimiento"
                    value={formData.fechaNacimiento}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Correo electrónico <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    name="correoElectronico"
                    value={formData.correoElectronico}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Teléfono Celular <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="tel"
                    name="telefonoCelular"
                    value={formData.telefonoCelular}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    placeholder="+52 55 1234 5678"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  RFC <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="rfc"
                    value={formData.rfc}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white uppercase"
                    placeholder="ABCD123456EF7"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Género <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="genero"
                    value={formData.genero}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona</option>
                    {generoOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Número de INE o Pasaporte <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="numeroINE"
                    value={formData.numeroINE}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Domicilio */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#5fc500] to-[#11653a] rounded-lg">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Domicilio</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Domicilio actual (Calle y Número) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <textarea
                    name="domicilioCalleNumero"
                    value={formData.domicilioCalleNumero}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white resize-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Número interior en caso de aplicar
                </label>
                <input
                  type="text"
                  name="numeroInterior"
                  value={formData.numeroInterior}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Colonia <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="colonia"
                  value={formData.colonia}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Código Postal <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="codigoPostal"
                  value={formData.codigoPostal}
                  onChange={handleInputChange}
                  maxLength={5}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Delegación o Municipio <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="delegacionMunicipio"
                  value={formData.delegacionMunicipio}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Ciudad <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Estado <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="estado"
                    value={formData.estado}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona un estado</option>
                    {estadosMexico.map((estado) => (
                      <option key={estado} value={estado}>
                        {estado}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="md:col-span-2 grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Tiempo viviendo en ese domicilio (años) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="tiempoViviendoAnos"
                    value={formData.tiempoViviendoAnos}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Tiempo viviendo (meses) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="tiempoViviendoMeses"
                    value={formData.tiempoViviendoMeses}
                    onChange={handleInputChange}
                    min="0"
                    max="11"
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Tipo de vivienda <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="tipoVivienda"
                    value={formData.tipoVivienda}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona</option>
                    {tipoViviendaOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Lugar de Nacimiento */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#11653a] to-[#096365] rounded-lg">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Lugar de Nacimiento
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  País de Nacimiento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="paisNacimiento"
                  value={formData.paisNacimiento}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                  placeholder="México"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Estado de Nacimiento <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="estadoNacimiento"
                    value={formData.estadoNacimiento}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona un estado</option>
                    {estadosMexico.map((estado) => (
                      <option key={estado} value={estado}>
                        {estado}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Información Laboral */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#096365] to-[#5ba49e] rounded-lg">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Información Laboral
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Empresa donde trabaja <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="empresaTrabaja"
                    value={formData.empresaTrabaja}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Estatus Laboral <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="estatusLaboral"
                    value={formData.estatusLaboral}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona</option>
                    {estatusLaboralOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Ocupación <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="ocupacion"
                    value={formData.ocupacion}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona</option>
                    {ocupacionOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Tipo de Industria <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="tipoIndustria"
                    value={formData.tipoIndustria}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona</option>
                    {tipoIndustriaOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Antigüedad <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="antiguedad"
                    value={formData.antiguedad}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    placeholder="Ej: 5 años"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Ingreso fijo mensual comprobable <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="ingresoFijoMensual"
                    value={formData.ingresoFijoMensual}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    placeholder="$50,000"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Información Financiera */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#5ba49e] to-[#218e01] rounded-lg">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Información Financiera
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ¿Eres socio Club Premier? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {siNoOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-2 p-3 border-2 border-slate-300 rounded-xl cursor-pointer hover:border-blue-600 hover:bg-blue-50 transition-all"
                    >
                      <input
                        type="radio"
                        name="esSocioClubPremier"
                        value={option}
                        checked={formData.esSocioClubPremier === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-2 border-slate-300 focus:ring-2 focus:ring-blue-600"
                        required
                      />
                      <span className="text-sm font-medium text-slate-700">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.esSocioClubPremier === "Sí" && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    ¿Cuál es tu número de socio? Si no tienes o no recuerdas responder N/A <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="numeroSocioClubPremier"
                      value={formData.numeroSocioClubPremier}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                      placeholder="Número de socio o N/A"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ¿Con cuáles tarjetas de crédito cuentas actualmente? (mencionar banco y tipo de tarjeta, por ejemplo: BBVA Oro) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <CreditCard className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <textarea
                    name="tarjetasCreditoActuales"
                    value={formData.tarjetasCreditoActuales}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white resize-none"
                    placeholder="Ej: BBVA Oro, Santander Free"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ¿Cuál es el límite de crédito en tu tarjeta con mayor crédito? <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="limiteCreditoMayor"
                    value={formData.limiteCreditoMayor}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    placeholder="$100,000"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Últimos 4 dígitos de tu tarjeta de CRÉDITO bancaria <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="ultimos4DigitosTarjeta"
                    value={formData.ultimos4DigitosTarjeta}
                    onChange={handleInputChange}
                    maxLength={4}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    placeholder="1234"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ¿Tienes actualmente Crédito Automotriz o has tenido alguno en los últimos 2 años? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {siNoOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-2 p-3 border-2 border-slate-300 rounded-xl cursor-pointer hover:border-blue-600 hover:bg-blue-50 transition-all"
                    >
                      <input
                        type="radio"
                        name="tieneCreditoAutomotriz"
                        value={option}
                        checked={formData.tieneCreditoAutomotriz === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-2 border-slate-300 focus:ring-2 focus:ring-blue-600"
                        required
                      />
                      <span className="text-sm font-medium text-slate-700">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ¿Tiene actualmente Crédito Hipotecario? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {siNoOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-2 p-3 border-2 border-slate-300 rounded-xl cursor-pointer hover:border-blue-600 hover:bg-blue-50 transition-all"
                    >
                      <input
                        type="radio"
                        name="tieneCreditoHipotecario"
                        value={option}
                        checked={formData.tieneCreditoHipotecario === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-2 border-slate-300 focus:ring-2 focus:ring-blue-600"
                        required
                      />
                      <span className="text-sm font-medium text-slate-700">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Si tiene el Score de Buró de Crédito ingresado aquí, nos ayudará a agilizar mucho más la solicitud. En caso de no contar con el, puede descargar la App: ZENFI y consultarlo en menos de 3 minutos. De caso contrario responder N/A <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="scoreBuroCredito"
                    value={formData.scoreBuroCredito}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white"
                    placeholder="Ej: 650 o N/A"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notas Adicionales */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Notas Adicionales
              </h2>
            </div>

            <textarea
              name="notas"
              value={formData.notas}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all font-medium text-sm bg-white resize-none"
              placeholder="Información adicional que consideres relevante..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => navigate("/cards")}
              className="flex-1 px-6 py-4 border-2 border-slate-300 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl font-bold text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enviando solicitud...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Enviar Solicitud
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AmexCardApplicationForm;

